import axios, { InternalAxiosRequestConfig } from 'axios';

import { AUTH_TOKEN_REFRESH, TIMEOUT, TOKEN_HEADER } from '@repo/constants';
import {
  getActiveAuthToken,
  getActiveRefreshToken,
  redirectAfterAuthExpired,
  setActiveAuthTokens,
} from './impersonation-session';

const host = import.meta.env.VITE_BACKEND_HOST;

export const $api = axios.create({
  baseURL: host,
  timeout: TIMEOUT,
  validateStatus: (status) => status < 400,
});

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
};

const clearAuthAndRedirect = () => {
  redirectAfterAuthExpired('/auth/login');
};

$api.interceptors.request.use(
  function (config) {
    const authToken = getActiveAuthToken();

    if (authToken) {
      config.headers.set('Authorization', TOKEN_HEADER + authToken);
    } else {
      config.headers.delete('Authorization');
    }

    // Tag every request with the app it originates from so the backend can
    // bucket audit rows (and any app-type-aware logic) as admin / portal /
    // client / mobile. Without this the middleware falls back to "portal".
    const appType = import.meta.env.VITE_APP_TYPE;
    if (appType) {
      config.headers.set('X-App-Type', appType);
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

$api.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const status = error.response?.status;
    const originalRequest = error.config as RetryableConfig | undefined;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      const refreshToken = getActiveRefreshToken();

      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.set('Authorization', TOKEN_HEADER + token);
          return $api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        axios
          .post(`${host}${AUTH_TOKEN_REFRESH}`, { refresh: refreshToken })
          .then(({ data }) => {
            setActiveAuthTokens(data.access, data.refresh);
            $api.defaults.headers.common['Authorization'] =
              TOKEN_HEADER + data.access;
            processQueue(null, data.access);
            originalRequest.headers.set(
              'Authorization',
              TOKEN_HEADER + data.access,
            );
            resolve($api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            clearAuthAndRedirect();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    if (status === 401) {
      clearAuthAndRedirect();
    }

    if (!error.response) {
      error.message = 'Network Connection Error';
    }

    return Promise.reject(error);
  },
);
